/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * external or speakeasy.
 */
export const EMAProviderKind = {
  External: "external",
  Speakeasy: "speakeasy",
} as const;
/**
 * external or speakeasy.
 */
export type EMAProviderKind = ClosedEnum<typeof EMAProviderKind>;

/**
 * Why the provider cannot serve now; omitted when it can. Identity chaining skips it and tries the next provider. not_trusted_by_endpoint means the endpoint's sign-in doesn't go through this provider, so it can't vouch for people there: an external issuer other than the sign-in's trusted issuer, or Speakeasy on a sign-in federated through an external issuer. readiness_unknown means the Speakeasy issuer's state could not be read just now; retry the read.
 */
export const UnavailableReason = {
  Deleted: "deleted",
  Disabled: "disabled",
  NotTrustedByEndpoint: "not_trusted_by_endpoint",
  SigningUnavailable: "signing_unavailable",
  NotRolledOut: "not_rolled_out",
  VerifiedDomainRequired: "verified_domain_required",
  SetupRequired: "setup_required",
  Keyless: "keyless",
  SigningPaused: "signing_paused",
  SignInEnforcementRequired: "sign_in_enforcement_required",
  ReadinessUnknown: "readiness_unknown",
} as const;
/**
 * Why the provider cannot serve now; omitted when it can. Identity chaining skips it and tries the next provider. not_trusted_by_endpoint means the endpoint's sign-in doesn't go through this provider, so it can't vouch for people there: an external issuer other than the sign-in's trusted issuer, or Speakeasy on a sign-in federated through an external issuer. readiness_unknown means the Speakeasy issuer's state could not be read just now; retry the read.
 */
export type UnavailableReason = ClosedEnum<typeof UnavailableReason>;

/**
 * One EMA provider a vendor accepts.
 */
export type EMAProvider = {
  /**
   * The organization's internal session issuer, for speakeasy.
   */
  internalSessionIssuerId?: string | undefined;
  /**
   * external or speakeasy.
   */
  kind: EMAProviderKind;
  /**
   * When the vendor last accepted an assertion from this provider anywhere in the organization, so the vendor already trusts it, from cached credentials that still hold an access token. Attributed to the exact issuer that vouched; the organization-wide default's external entry takes the newest acceptance of any external issuer. Set on every response that returns an order; omitted when the vendor has never accepted it, when acceptance could not be read, or on readEMA when the caller lacks org:read.
   */
  lastAcceptedAt?: Date | undefined;
  /**
   * The external provider's remote session issuer. Omitted for speakeasy, and in the organization-wide default order, where an external entry stands for each endpoint's own trusted issuer.
   */
  remoteSessionIssuerId?: string | undefined;
  /**
   * Why the provider cannot serve now; omitted when it can. Identity chaining skips it and tries the next provider. not_trusted_by_endpoint means the endpoint's sign-in doesn't go through this provider, so it can't vouch for people there: an external issuer other than the sign-in's trusted issuer, or Speakeasy on a sign-in federated through an external issuer. readiness_unknown means the Speakeasy issuer's state could not be read just now; retry the read.
   */
  unavailableReason?: UnavailableReason | undefined;
};

/** @internal */
export const EMAProviderKind$inboundSchema: z.ZodMiniEnum<
  typeof EMAProviderKind
> = z.enum(EMAProviderKind);

/** @internal */
export const UnavailableReason$inboundSchema: z.ZodMiniEnum<
  typeof UnavailableReason
> = z.enum(UnavailableReason);

/** @internal */
export const EMAProvider$inboundSchema: z.ZodMiniType<EMAProvider, unknown> = z
  .pipe(
    z.object({
      internal_session_issuer_id: z.optional(z.string()),
      kind: EMAProviderKind$inboundSchema,
      last_accepted_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      remote_session_issuer_id: z.optional(z.string()),
      unavailable_reason: z.optional(UnavailableReason$inboundSchema),
    }),
    z.transform((v) => {
      return remap$(v, {
        "internal_session_issuer_id": "internalSessionIssuerId",
        "last_accepted_at": "lastAcceptedAt",
        "remote_session_issuer_id": "remoteSessionIssuerId",
        "unavailable_reason": "unavailableReason",
      });
    }),
  );

export function emaProviderFromJSON(
  jsonString: string,
): SafeParseResult<EMAProvider, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => EMAProvider$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'EMAProvider' from JSON`,
  );
}
